import {
  DEFAULT_LOCATION_COORDS,
  hasValidSavedCoordinates,
} from 'config/defaultLocation.config.js';
import { getDynamicDeviceIcon } from 'utils/devices/icon.utils';
import { getBuoyLeafletIcon } from '../../Buoy/map/buoyTemperatureLegendUtils';
import { isBuoyDevice } from '../../Buoy/utils/buoyDeviceUtils';

export const parseCoordinate = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : null;
};

export const getLocationCoordinates = (location) => {
  if (!location) return null;
  const lat = parseCoordinate(location.lat);
  const lng = parseCoordinate(location.lng);
  return lat !== null && lng !== null ? [lat, lng] : null;
};

export const getMapCoords = (latitude, longitude) => {
  const lat = parseCoordinate(latitude);
  const lng = parseCoordinate(longitude);
  return hasValidSavedCoordinates(lat, lng) ? [lat, lng] : [...DEFAULT_LOCATION_COORDS];
};

export const getIcon = (device) => {
  if (isBuoyDevice(device)) {
    return getBuoyLeafletIcon(device, [50, 50]);
  }
  return getDynamicDeviceIcon(device, 'icon');
};

/**
 * Returns the Leaflet-compatible bounds that frame all devices, or null if
 * none have valid coordinates.
 * @param {object[]} devices - Array of device objects with location data.
 * @returns {[[number, number], [number, number]] | null} SW and NE corner pairs
 *   as `[[minLat, minLng], [maxLat, maxLng]]`, or null if no valid coordinates exist.
 */
export const getGroupMapBounds = (devices) => {
  if (!Array.isArray(devices) || devices.length === 0) return null;
  const coords = devices.map((d) => getLocationCoordinates(d?.location)).filter(Boolean);
  if (!coords.length) return null;
  const lats = coords.map((c) => c[0]);
  const lngs = coords.map((c) => c[1]);
  return [
    [Math.min(...lats), Math.min(...lngs)],
    [Math.max(...lats), Math.max(...lngs)],
  ];
};

/**
 * Given an array of device objects, returns the map view needed to frame them all.
 * @param {object[]} devices - Array of device objects with location data.
 * @returns {{ type: 'bounds', bounds: [[number, number], [number, number]] }
 *   | { type: 'center', center: [number, number], zoom: number }
 *   | null} Bounds view when devices span multiple coordinates, center+zoom when
 *   all devices share one point, or null if no devices have valid coordinates.
 */
export const resolveGroupMapView = (devices) => {
  const bounds = getGroupMapBounds(devices);
  if (!bounds) return null;
  const [[minLat, minLng], [maxLat, maxLng]] = bounds;
  if (minLat === maxLat && minLng === maxLng) {
    return { type: 'center', center: [minLat, minLng], zoom: 16 };
  }
  return { type: 'bounds', bounds };
};
